import React, { useState, useEffect } from 'react';
import {
  Card,
  Row,
  Col,
  Alert,
  Table,
  Badge,
  message,
  Button,
  Space,
  Statistic,
  Typography,
  Tag,
  List,
  Empty,
  Spin
} from 'antd';
import {
  ReloadOutlined,
  DownloadOutlined,
  DashboardOutlined,
  FileTextOutlined,
  UserOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  WarningOutlined,
  TrophyOutlined,
  RiseOutlined,
  FallOutlined,
  EyeOutlined,
  CloseCircleOutlined,
  CalendarOutlined,
  BellOutlined
} from '@ant-design/icons';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import apiClient, { handleApiError } from '../utils/axiosConfig';
import * as XLSX from 'xlsx';
import {
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';

const { Title, Text } = Typography;

const AdminMain = () => {
  const { t } = useTranslation();

  const [loading, setLoading] = useState(false);
  const [statistics, setStatistics] = useState(null);
  const [certificateTrend, setCertificateTrend] = useState([]);
  const [recentActivities, setRecentActivities] = useState([]);
  const [certificates, setCertificates] = useState([]);
  const [usersMissingCertificates, setUsersMissingCertificates] = useState([]);
  const [upcomingAudits, setUpcomingAudits] = useState([]);
  const [nextAudit, setNextAudit] = useState(null);
  const [certificateNotifications, setCertificateNotifications] = useState([]);
  const [performanceData, setPerformanceData] = useState([]);
  const [supplierClassDistribution, setSupplierClassDistribution] = useState([]);
  const [performanceLevelData, setPerformanceLevelData] = useState([]);

  const fetchDashboardData = async () => {
    setLoading(true);

    try {
      const [
        statsResponse,
        trendResponse,
        activitiesResponse,
        pendingCertsResponse,
        allSuppliersResponse,
        allCertificatesResponse,
        auditsResponse,
        performanceResponse
      ] = await Promise.all([
        apiClient.get('/api/Dashboard/statistics'),
        apiClient.get('/api/Dashboard/certificate-trend?months=6'),
        apiClient.get('/api/Dashboard/recent-activities?count=10'),
        apiClient.get('/api/Certificates/pending'),
        apiClient.get('/api/Suppliers/suppliers-only'),
        apiClient.get('/api/Certificates'),
        apiClient.get('/api/SupplierAudit'),
        apiClient.get('/api/SupplierPerformance')
      ]);

      // ✅ 1. Statistics
      setStatistics(statsResponse.data);

      // ✅ 2. Certificate Trend
      const trendData = trendResponse.data?.data || [];
      const formattedTrend = trendData.map(item => ({
        month: `${item.year}-${String(item.month).padStart(2, '0')}`,
        count: item.total || 0,
        approved: item.approved || 0,
        pending: item.pending || 0
      }));
      setCertificateTrend(formattedTrend);

      // ✅ 3. Recent Activities
      setRecentActivities(activitiesResponse.data?.activities || []);

      // ✅ 4. Pending Certificates
      const pendingCerts = Array.isArray(pendingCertsResponse.data)
        ? pendingCertsResponse.data
        : pendingCertsResponse.data?.$values || [];
      setCertificates(pendingCerts);

      // ✅ 5. Suppliers
      const allSuppliers = Array.isArray(allSuppliersResponse.data)
        ? allSuppliersResponse.data
        : allSuppliersResponse.data?.$values || [];

      // ✅ 6. All Certificates
      const allCertificates = Array.isArray(allCertificatesResponse.data)
        ? allCertificatesResponse.data
        : allCertificatesResponse.data?.data || allCertificatesResponse.data?.$values || [];

      // ✅ 7. Missing Certificates
      const requiredCerts = ['IATF 16949', 'ISO 14001'];

      const suppliersWithMissingCerts = allSuppliers
        .map((supplier) => {
          const supplierCerts = allCertificates.filter(
            (cert) => cert.supplierId === supplier.supplierId
          );

          const missingCerts = requiredCerts.filter(
            (reqCert) => !supplierCerts.some((cert) => cert.certificateName === reqCert)
          );

          return {
            supplierId: supplier.supplierId,
            supplierName: supplier.supplierName,
            supplierCode: supplier.supplierCode,
            businessEmail: supplier.businessEmail,
            missingCertificates: missingCerts.join(', '),
          };
        })
        .filter((supplier) => supplier.missingCertificates);

      setUsersMissingCertificates(suppliersWithMissingCerts);

      // ✅ 8. Audits
      const audits = Array.isArray(auditsResponse.data)
        ? auditsResponse.data
        : auditsResponse.data?.$values || [];

      const auditsWithSupplier = audits
        .filter((audit) => audit.supplier)
        .map((audit) => ({
          auditId: audit.supplierAuditId,
          supplierId: audit.supplierId,
          userName: audit.supplier?.supplierName || t('unknown'),
          auditDate: audit.auditDate,
        }));

      const today = moment();
      const in30Days = moment().add(30, 'days');

      const upcoming = auditsWithSupplier
        .filter((audit) => {
          const auditDate = moment(audit.auditDate);
          return auditDate.isAfter(today) && auditDate.isBefore(in30Days);
        })
        .map((audit) => ({
          ...audit,
          status: t('upcoming'),
          color: 'warning',
        }));

      setUpcomingAudits(upcoming);

      const closestAudit = auditsWithSupplier
        .filter((audit) => moment(audit.auditDate).isAfter(today))
        .sort((a, b) => moment(a.auditDate).diff(moment(b.auditDate)))[0];

      setNextAudit(closestAudit || null);

      // ✅ 9. Certificate Notifications
      const notifications = [];
      const todayMoment = moment();

      allSuppliers.forEach((supplier) => {
        const supplierCerts = allCertificates.filter((cert) => cert.supplierId === supplier.supplierId);

        const missingCerts = requiredCerts.filter(
          (reqCert) => !supplierCerts.some((cert) => cert.certificateName === reqCert)
        );

        if (missingCerts.length > 0) {
          notifications.push({
            text: t('missing_certificates_notification', {
              name: supplier.supplierName,
              code: supplier.supplierCode,
              certs: missingCerts.join(', ')
            }),
            type: 'error',
          });
        }

        supplierCerts.forEach((cert) => {
          const validityDate = moment(cert.certificateValidityDate);
          const daysUntilExpiry = validityDate.diff(todayMoment, 'days');

          if (daysUntilExpiry < 0) {
            notifications.push({
              text: t('expired_certificate_notification', {
                cert: cert.certificateName,
                name: supplier.supplierName,
                code: supplier.supplierCode,
                date: validityDate.format('DD/MM/YYYY')
              }),
              type: 'error',
            });
          } else if (daysUntilExpiry <= 30) {
            notifications.push({
              text: t('expiring_certificate_notification', {
                cert: cert.certificateName,
                name: supplier.supplierName,
                code: supplier.supplierCode,
                days: daysUntilExpiry,
                date: validityDate.format('DD/MM/YYYY')
              }),
              type: 'warning',
            });
          }
        });
      });

      setCertificateNotifications(notifications);

      // ✅ 10. Performance Data
      const performanceDataRaw = Array.isArray(performanceResponse.data)
        ? performanceResponse.data
        : performanceResponse.data?.$values || [];

      setPerformanceData(performanceDataRaw);

      // ✅ 11. Supplier Class Distribution
      const uniqueSupplierPerformance = {};

      performanceDataRaw.forEach(perf => {
        const supplierId = perf.supplierId;
        const period = perf.period;

        if (!uniqueSupplierPerformance[supplierId] ||
          period > uniqueSupplierPerformance[supplierId].period) {
          uniqueSupplierPerformance[supplierId] = perf;
        }
      });

      const latestPerformances = Object.values(uniqueSupplierPerformance);

      const classDistribution = [
        {
          name: t('class_a_suppliers'),
          value: latestPerformances.filter(p => p.supplierClass === 'A').length,
          color: '#10b981'
        },
        {
          name: t('class_b_suppliers'),
          value: latestPerformances.filter(p => p.supplierClass === 'B').length,
          color: '#f59e0b'
        },
        {
          name: t('class_c_suppliers'),
          value: latestPerformances.filter(p => p.supplierClass === 'C').length,
          color: '#ef4444'
        }
      ];

      setSupplierClassDistribution(classDistribution);

      // ✅ 12. Performance Level Distribution
      const performanceLevels = [
        {
          level: t('excellent') + ' (90-100)',
          count: latestPerformances.filter(p => p.totalScore >= 90).length,
          color: '#10b981'
        },
        {
          level: t('good') + ' (80-89)',
          count: latestPerformances.filter(p => p.totalScore >= 80 && p.totalScore < 90).length,
          color: '#3b82f6'
        },
        {
          level: 'Average (70-79)',
          count: latestPerformances.filter(p => p.totalScore >= 70 && p.totalScore < 80).length,
          color: '#f59e0b'
        },
        {
          level: t('needs_improvement') + ' (<70)',
          count: latestPerformances.filter(p => p.totalScore < 70).length,
          color: '#ef4444'
        }
      ];

      setPerformanceLevelData(performanceLevels);

    } catch (error) {
      const errorMessage = handleApiError(error, t('data_fetch_error'));
      message.error(errorMessage);
      console.error('❌ Dashboard error:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const handleViewCertificate = async (certificateId) => {
    try {
      const response = await apiClient.get(`/api/Certificates/${certificateId}/sas`);
      const sasUrl = response.data.sasUrl;

      if (sasUrl) {
        window.open(sasUrl, '_blank');
      } else {
        message.error(t('sas_url_error'));
      }
    } catch (error) {
      const errorMessage = handleApiError(error, t('certificate_fetch_error'));
      message.error(errorMessage);
    }
  };

  const approveCertificate = async (certificateId) => {
    try {
      await apiClient.put(`/api/Certificates/${certificateId}/approve`);
      message.success(t('certificate_approved'));
      setCertificates((prev) => prev.filter((cert) => cert.certificateId !== certificateId));
      fetchDashboardData();
    } catch (error) {
      const errorMessage = handleApiError(error, t('certificate_approve_error'));
      message.error(errorMessage);
    }
  };

  const handleExportExcel = () => {
    try {
      const wb = XLSX.utils.book_new();

      const certsData = certificates.map((cert) => ({
        [t('certificate')]: cert.certificateName,
        [t('supplier')]: cert.supplierName,
        [t('supplier_code')]: cert.supplierCode,
        [t('validity_date')]: moment(cert.certificateValidityDate).format('DD/MM/YYYY'),
        [t('status')]: cert.situation === '0' ? t('pending') : t('approved'),
      }));

      const ws1 = XLSX.utils.json_to_sheet(certsData);
      XLSX.utils.book_append_sheet(wb, ws1, t('pending_certificate_approvals'));

      const missingData = usersMissingCertificates.map((user) => ({
        [t('supplier_name')]: user.supplierName,
        [t('supplier_code')]: user.supplierCode,
        [t('business_email')]: user.businessEmail,
        [t('missing_certificates_col')]: user.missingCertificates,
      }));

      const ws2 = XLSX.utils.json_to_sheet(missingData);
      XLSX.utils.book_append_sheet(wb, ws2, t('suppliers_with_missing_certificates'));

      const auditsData = upcomingAudits.map((audit) => ({
        [t('supplier')]: audit.userName,
        [t('audit_date')]: moment(audit.auditDate).format('DD/MM/YYYY'),
        [t('status')]: audit.status,
      }));

      const ws3 = XLSX.utils.json_to_sheet(auditsData);
      XLSX.utils.book_append_sheet(wb, ws3, t('upcoming_audits_next_30_days'));

      XLSX.writeFile(wb, `Dashboard_Report_${moment().format('YYYY-MM-DD')}.xlsx`);
      message.success(t('excel_exported_successfully'));
    } catch (error) {
      message.error(t('data_fetch_error'));
      console.error('Export error:', error);
    }
  };

  const calculateAverageScore = () => {
    const uniqueSupplierPerformance = {};

    performanceData.forEach(perf => {
      const supplierId = perf.supplierId;
      const period = perf.period;

      if (!uniqueSupplierPerformance[supplierId] ||
        period > uniqueSupplierPerformance[supplierId].period) {
        uniqueSupplierPerformance[supplierId] = perf;
      }
    });

    const latestPerformances = Object.values(uniqueSupplierPerformance);

    if (latestPerformances.length === 0) return 0;

    const total = latestPerformances.reduce((sum, p) => sum + (p.totalScore || 0), 0);
    return (total / latestPerformances.length).toFixed(2);
  };

  const certificateColumns = [
    {
      title: t('certificate'),
      dataIndex: 'certificateName',
      key: 'certificateName',
      render: (text) => <Text strong>{text}</Text>
    },
    {
      title: t('supplier'),
      key: 'supplier',
      render: (_, record) => (
        <div>
          <div style={{ fontWeight: '600' }}>{record.supplierName}</div>
          <div style={{ fontSize: '12px', color: '#8c8c8c' }}>{record.supplierCode}</div>
        </div>
      )
    },
    {
      title: t('validity_date'),
      dataIndex: 'certificateValidityDate',
      key: 'certificateValidityDate',
      render: (text) => {
        const date = moment(text);
        const daysUntilExpiry = date.diff(moment(), 'days');
        return (
          <div>
            <div>{date.format('DD/MM/YYYY')}</div>
            {daysUntilExpiry < 30 && (
              <Tag color={daysUntilExpiry < 0 ? 'error' : 'warning'} style={{ marginTop: '4px' }}>
                {daysUntilExpiry < 0 ? t('expired') : `${daysUntilExpiry} ${t('days_left')}`}
              </Tag>
            )}
          </div>
        );
      }
    },
    {
      title: t('status'),
      dataIndex: 'situation',
      key: 'situation',
      render: (situation) => (
        <Tag
          color={situation === '0' ? 'warning' : 'success'}
          icon={situation === '0' ? <ClockCircleOutlined /> : <CheckCircleOutlined />}
        >
          {situation === '0' ? t('pending') : t('approved')}
        </Tag>
      )
    },
    {
      title: t('action'),
      key: 'action',
      render: (_, record) => (
        <Space>
          <Button
            type="primary"
            size="small"
            icon={<EyeOutlined />}
            onClick={() => handleViewCertificate(record.certificateId)}
          >
            {t('view')}
          </Button>
          <Button
            type="default"
            size="small"
            style={{ color: '#52c41a', borderColor: '#52c41a' }}
            icon={<CheckCircleOutlined />}
            onClick={() => approveCertificate(record.certificateId)}
          >
            {t('approve')}
          </Button>
        </Space>
      )
    }
  ];

  const missingCertificatesColumns = [
    {
      title: t('supplier'),
      key: 'supplier',
      render: (_, record) => (
        <div>
          <div style={{ fontWeight: '600' }}>{record.supplierName}</div>
          <div style={{ fontSize: '12px', color: '#8c8c8c' }}>{record.supplierCode}</div>
        </div>
      )
    },
    {
      title: t('business_email'),
      dataIndex: 'businessEmail',
      key: 'businessEmail'
    },
    {
      title: t('missing_certificates_col'),
      dataIndex: 'missingCertificates',
      key: 'missingCertificates',
      render: (text) => (
        <Space wrap>
          {text.split(', ').map((cert, index) => (
            <Tag key={index} color="error" icon={<CloseCircleOutlined />}>
              {cert}
            </Tag>
          ))}
        </Space>
      )
    }
  ];

  const upcomingAuditsColumns = [
    {
      title: t('supplier'),
      dataIndex: 'userName',
      key: 'userName',
      render: (text) => <Text strong>{text}</Text>
    },
    {
      title: t('audit_date'),
      dataIndex: 'auditDate',
      key: 'auditDate',
      render: (text) => {
        const date = moment(text);
        const daysUntil = date.diff(moment(), 'days');
        return (
          <div>
            <div>{date.format('DD/MM/YYYY')}</div>
            <Tag color="blue" style={{ marginTop: '4px' }}>
              {daysUntil} {t('days_until')}
            </Tag>
          </div>
        );
      }
    },
    {
      title: t('status'),
      key: 'status',
      render: (_, record) => <Badge status={record.color} text={record.status} />
    }
  ];

  return (
    <div style={{ padding: '24px', backgroundColor: '#f0f2f5', minHeight: '100vh' }}>
      {/* Header */}
      <div style={{
        background: '#fff',
        padding: '32px',
        borderRadius: '12px',
        marginBottom: '24px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
        borderLeft: '4px solid #1890ff'
      }}>
        <Row justify="space-between" align="middle">
          <Col>
            <Title level={2} style={{ margin: 0, color: '#262626' }}>
              <DashboardOutlined style={{ marginRight: '12px', color: '#1890ff' }} />
              {t('admin_dashboard')}
            </Title>
            <Text type="secondary" style={{ fontSize: '15px' }}>
              {t('welcome_back')} - {moment().format('DD MMMM YYYY')}
            </Text>
          </Col>
          <Col>
            <Space size="middle">
              <Button
                icon={<DownloadOutlined />}
                size="large"
                onClick={handleExportExcel}
              >
                {t('export_excel')}
              </Button>
              <Button
                icon={<ReloadOutlined />}
                size="large"
                onClick={fetchDashboardData}
                loading={loading}
              >
                {t('refresh')}
              </Button>
            </Space>
          </Col>
        </Row>
      </div>

      {/* Statistics Cards */}
      <Row gutter={16} style={{ marginBottom: '24px' }}>
        <Col xs={24} sm={12} md={6}>
          <Card style={{ borderRadius: '12px', background: '#6366f1', border: 'none' }}>
            <Statistic
              title={<span style={{ color: '#fff', fontSize: '14px' }}>{t('total_suppliers')}</span>}
              value={statistics?.suppliers?.total || 0}
              prefix={<UserOutlined style={{ color: '#fff' }} />}
              valueStyle={{ color: '#fff', fontSize: '32px', fontWeight: '700' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card style={{ borderRadius: '12px', background: '#8b5cf6', border: 'none' }}>
            <Statistic
              title={<span style={{ color: '#fff', fontSize: '14px' }}>{t('total_certificates')}</span>}
              value={statistics?.certificates?.total || 0}
              prefix={<FileTextOutlined style={{ color: '#fff' }} />}
              valueStyle={{ color: '#fff', fontSize: '32px', fontWeight: '700' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card style={{ borderRadius: '12px', background: '#06b6d4', border: 'none' }}>
            <Statistic
              title={<span style={{ color: '#fff', fontSize: '14px' }}>{t('pending_approvals')}</span>}
              value={certificates.length}
              prefix={<ClockCircleOutlined style={{ color: '#fff' }} />}
              valueStyle={{ color: '#fff', fontSize: '32px', fontWeight: '700' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card style={{ borderRadius: '12px', background: '#f59e0b', border: 'none' }}>
            <Statistic
              title={<span style={{ color: '#fff', fontSize: '14px' }}>{t('upcoming_audits')}</span>}
              value={upcomingAudits.length}
              prefix={<CalendarOutlined style={{ color: '#fff' }} />}
              valueStyle={{ color: '#fff', fontSize: '32px', fontWeight: '700' }}
            />
          </Card>
        </Col>
      </Row>

      {/* Performance Overview */}
      <Row gutter={16} style={{ marginBottom: '24px' }}>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title={t('average_supplier_score')}
              value={calculateAverageScore()}
              suffix="/ 100"
              prefix={<TrophyOutlined />}
              valueStyle={{ color: '#722ed1' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title={t('class_a_suppliers')}
              value={supplierClassDistribution.find(s => s.name === t('class_a_suppliers'))?.value || 0}
              prefix={<RiseOutlined />}
              valueStyle={{ color: '#10b981' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title={t('class_b_suppliers')}
              value={supplierClassDistribution.find(s => s.name === t('class_b_suppliers'))?.value || 0}
              prefix={<WarningOutlined />}
              valueStyle={{ color: '#f59e0b' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title={t('class_c_suppliers')}
              value={supplierClassDistribution.find(s => s.name === t('class_c_suppliers'))?.value || 0}
              prefix={<FallOutlined />}
              valueStyle={{ color: '#ef4444' }}
            />
          </Card>
        </Col>
      </Row>

      {/* Charts */}
      <Row gutter={16} style={{ marginBottom: '24px' }}>
        {/* Supplier Performance Levels - Column Chart */}
        <Col xs={24} lg={12}>
          <Card
            title={
              <Space>
                <TrophyOutlined style={{ color: '#1890ff' }} />
                <span>{t('supplier_performance_levels')}</span>
              </Space>
            }
            bordered={false}
            style={{ borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}
          >
            {loading ? (
              <div style={{ textAlign: 'center', padding: '40px' }}>
                <Spin size="large" />
              </div>
            ) : performanceLevelData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={performanceLevelData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="level" angle={-15} textAnchor="end" height={80} />
                  <YAxis />
                  <RechartsTooltip />
                  <Legend />
                  <Bar dataKey="count" name={t('total_suppliers')} radius={[8, 8, 0, 0]}>
                    {performanceLevelData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <Empty description={t('no_performance_data_available')} />
            )}
          </Card>
        </Col>

        {/* Supplier Class Distribution - Pie Chart */}
        <Col xs={24} lg={12}>
          <Card
            title={
              <Space>
                <RiseOutlined style={{ color: '#1890ff' }} />
                <span>{t('supplier_class_distribution')}</span>
              </Space>
            }
            bordered={false}
            style={{ borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}
          >
            {loading ? (
              <div style={{ textAlign: 'center', padding: '40px' }}>
                <Spin size="large" />
              </div>
            ) : supplierClassDistribution.some(s => s.value > 0) ? (
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={supplierClassDistribution.filter(s => s.value > 0)}
                    cx="50%"
                    cy="50%"
                    labelLine={true}
                    label={({ name, value, percent }) => `${name}: ${value} (${(percent * 100).toFixed(0)}%)`}
                    outerRadius={100}
                    innerRadius={60}
                    fill="#8884d8"
                    dataKey="value"
                    paddingAngle={5}
                  >
                    {supplierClassDistribution.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <RechartsTooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <Empty description={t('no_supplier_performance_data_available')} />
            )}
          </Card>
        </Col>
      </Row>

      {/* Notifications & Recent Activities */}
      <Row gutter={16} style={{ marginBottom: '24px' }}>
        {/* Notifications */}
        <Col xs={24} lg={12}>
          <Card
            title={
              <Space>
                <BellOutlined style={{ color: '#ff4d4f' }} />
                <span>{t('notifications')}</span>
                <Badge count={certificateNotifications.length} showZero style={{ backgroundColor: '#ff4d4f' }} />
              </Space>
            }
            bordered={false}
            style={{
              borderRadius: '12px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
              height: '450px',
              display: 'flex',
              flexDirection: 'column'
            }}
            bodyStyle={{
              flex: 1,
              overflowY: 'auto',
              padding: '16px'
            }}
          >
            {loading ? (
              <div style={{ textAlign: 'center', padding: '40px' }}>
                <Spin />
              </div>
            ) : certificateNotifications.length > 0 ? (
              <List
                dataSource={certificateNotifications}
                renderItem={(note) => (
                  <List.Item style={{ padding: '12px 0', border: 'none' }}>
                    <Alert
                      message={note.text}
                      type={note.type}
                      showIcon
                      style={{ width: '100%' }}
                    />
                  </List.Item>
                )}
              />
            ) : (
              <Empty description={t('no_notifications')} style={{ marginTop: '60px' }} />
            )}
          </Card>
        </Col>

        {/* Recent Activities */}
        <Col xs={24} lg={12}>
          <Card
            title={
              <Space>
                <ClockCircleOutlined style={{ color: '#1890ff' }} />
                <span>{t('recent_activities')}</span>
              </Space>
            }
            bordered={false}
            style={{
              borderRadius: '12px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
              height: '450px',
              display: 'flex',
              flexDirection: 'column'
            }}
            bodyStyle={{
              flex: 1,
              overflowY: 'auto',
              padding: '16px'
            }}
          >
            {loading ? (
              <div style={{ textAlign: 'center', padding: '40px' }}>
                <Spin />
              </div>
            ) : recentActivities.length > 0 ? (
              <List
                dataSource={recentActivities}
                renderItem={(activity) => (
                  <List.Item style={{ padding: '12px 0' }}>
                    <List.Item.Meta
                      avatar={<FileTextOutlined style={{ fontSize: '20px', color: '#1890ff' }} />}
                      title={<Text strong>{activity.title}</Text>}
                      description={
                        <div>
                          <div>{activity.description}</div>
                          <Text type="secondary" style={{ fontSize: '12px' }}>
                            {moment(activity.timestamp).format('DD/MM/YYYY HH:mm')}
                          </Text>
                        </div>
                      }
                    />
                  </List.Item>
                )}
              />
            ) : (
              <Empty description={t('no_recent_activities')} style={{ marginTop: '60px' }} />
            )}
          </Card>
        </Col>
      </Row>

      {/* Pending Certificates */}
      <Card
        title={
          <Space>
            <ClockCircleOutlined style={{ color: '#faad14' }} />
            <span>{t('pending_certificate_approvals')}</span>
            <Badge count={certificates.length} showZero style={{ backgroundColor: '#faad14' }} />
          </Space>
        }
        bordered={false}
        style={{ marginBottom: '24px', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}
      >
        <Table
          dataSource={certificates}
          columns={certificateColumns}
          rowKey="certificateId"
          pagination={{ pageSize: 5 }}
          loading={loading}
          locale={{ emptyText: <Empty description={t('no_pending_certificates')} /> }}
        />
      </Card>

      {/* Missing Certificates */}
      <Card
        title={
          <Space>
            <WarningOutlined style={{ color: '#ff4d4f' }} />
            <span>{t('suppliers_with_missing_certificates')}</span>
            <Badge count={usersMissingCertificates.length} showZero style={{ backgroundColor: '#ff4d4f' }} />
          </Space>
        }
        bordered={false}
        style={{ marginBottom: '24px', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}
      >
        <Table
          dataSource={usersMissingCertificates}
          columns={missingCertificatesColumns}
          rowKey="supplierId"
          pagination={{ pageSize: 5 }}
          loading={loading}
          locale={{ emptyText: <Empty description={t('all_suppliers_have_required_certificates')} /> }}
        />
      </Card>

      {/* Upcoming Audits */}
      <Card
        title={
          <Space>
            <CalendarOutlined style={{ color: '#1890ff' }} />
            <span>{t('upcoming_audits_next_30_days')}</span>
            <Badge count={upcomingAudits.length} showZero style={{ backgroundColor: '#1890ff' }} />
          </Space>
        }
        bordered={false}
        style={{ borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}
      >
        {upcomingAudits.length > 0 ? (
          <Table
            dataSource={upcomingAudits}
            columns={upcomingAuditsColumns}
            rowKey="auditId"
            pagination={{ pageSize: 5 }}
            loading={loading}
          />
        ) : (
          <Empty
            description={
              nextAudit
                ? `${t('next_audit_info')}: ${nextAudit.userName} - ${moment(nextAudit.auditDate).format('DD/MM/YYYY')}`
                : t('no_upcoming_audits_in_30_days')
            }
          />
        )}
      </Card>
    </div>
  );
};

export default AdminMain;
